import { Button, Group, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { match, P } from "ts-pattern";
import { routes } from "@/configs/router.config/routes";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Tag } from "@/models/tag";

interface Props {
  submit: (name: string, description: string) => Promise<void>;
  isLoading: boolean;
  isUpdate?: boolean;
  tag?: Tag;
}

interface Form {
  name: string;
  description: string;
}

const TagEditForm: React.FC<Props> = ({ submit, isLoading, isUpdate, tag }) => {
  const { t } = useTranslation("admin");
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  const form = useForm<Form>({
    initialValues: {
      name: tag?.name ?? "",
      description: tag?.description ?? "",
    },
    validate: {
      name: (value) => {
        if (isUpdate) return null;
        return match(value)
          .with(
            P.when((v) => v.length === 0),
            () => t("tag.validation.nameRequired"),
          )
          .with(
            P.when((v) => v.length > 50),
            () => t("tag.validation.nameTooLong"),
          )
          .otherwise(() => null);
      },
      description: (value) =>
        match(value)
          .with(
            P.when((v) => v.length === 0),
            () => t("tag.validation.descriptionRequired"),
          )
          .with(
            P.when((v) => v.length > 1000),
            () => t("tag.validation.descriptionTooLong"),
          )
          .otherwise(() => null),
    },
  });

  const handleClickSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) return;

    await submit(form.values.name, form.values.description);
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} noValidate>
      <Stack gap={20}>
        <TextInput
          data-testid="name-input"
          label={t("tag.form.name")}
          key={form.key("name")}
          w={300}
          required={!isUpdate}
          disabled={isUpdate}
          {...form.getInputProps("name")}
        />
        <Textarea
          data-testid="description-input"
          label={t("tag.form.description")}
          key={form.key("description")}
          w={500}
          minRows={4}
          maxRows={8}
          autosize
          required
          {...form.getInputProps("description")}
        />
      </Stack>

      <Group>
        <Link to={routes.tagList(sessionCurrentWorkspaceId)}>
          <Button mt="25px" variant="outline" color="gray">
            {t("tag.form.cancel")}
          </Button>
        </Link>

        <Button
          data-testid="submit-button"
          disabled={isLoading}
          onClick={handleClickSubmit}
          mt="25px"
        >
          {isUpdate ? t("tag.form.update") : t("tag.form.create")}
        </Button>
      </Group>
    </form>
  );
};

export default TagEditForm;
